/* eslint-disable */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import type { TDocumentDefinitions, Content, Margins } from 'pdfmake/interfaces';
(pdfMake as any).vfs = (pdfFonts as any).vfs;

import { declaracaoRecebimento, IComprovanteBaixaDados, linhaAssinaturaRecebedor, textosComprovante } from './ComprovanteBaixa';

const formatarMoeda = (valor: number) => Number(valor || 0).toFixed(2);

// Mesmo formato 80mm de impressaotermica.ts (PDVSIAC): sem tabelas com
// borda (não rendem bem em papel estreito), só colunas simples e linhas
// tracejadas como separador.
// `demais`: mais recibos no mesmo cupom (ex.: recibo de todas as parcelas),
// separados por uma linha de corte — papel em rolo, sem quebra de página.
export async function gerarComprovanteBaixaTermica(dados: IComprovanteBaixaDados, demais: IComprovanteBaixaDados[] = []): Promise<Blob> {
  // `defaultStyle` abaixo referencia a fonte 'Roboto' pelo nome — precisa
  // registrar explicitamente (mesmo padrão de impressaovenda.ts/impressaovenda01.ts/
  // impressaovenda88.ts, os outros geradores pdfmake com defaultStyle.font
  // deste projeto), senão o pdfmake não sabe qual arquivo do vfs usar pra
  // cada variante (normal/bold/...).
  pdfMake.fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
  };

  const montarConteudo = (dados: IComprovanteBaixaDados): Content[] => {
    const content: Content[] = [];
    const textos = textosComprovante(dados);

    content.push({
      text: dados.contaNome,
      bold: true,
      fontSize: 11,
      alignment: 'center',
      margin: [0, 0, 0, 2],
    });
    content.push({
      text: textos.titulo,
      bold: true,
      fontSize: 9,
      alignment: 'center',
      margin: [0, 0, 0, 4],
    });
    content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 0, 0, 4] });

    content.push({ text: `${textos.parceiro}: ${dados.parceiro}`, fontSize: 8, margin: [0, 0, 0, 1] });
    content.push({ text: `Data: ${moment(dados.datalan).format('DD/MM/YYYY')}`, fontSize: 8, margin: [0, 0, 0, 4] });
    content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 0, 0, 4] });

    content.push({ text: textos.contas, bold: true, fontSize: 8, margin: [0, 0, 0, 2] });
    dados.contas.forEach((conta) => {
      content.push({
        columns: [
          { width: '*', text: `${conta.numero || '***'}${conta.parcela ? ` (${conta.parcela})` : ''}`, fontSize: 8 },
          { width: 60, text: formatarMoeda(conta.valor), fontSize: 8, alignment: 'right' },
        ],
        margin: [0, 0, 0, 1],
      });
    });
    content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 4, 0, 4] });

    content.push({ text: 'FORMAS DE PAGAMENTO', bold: true, fontSize: 8, margin: [0, 0, 0, 2] });
    dados.formasPagamento.forEach((forma) => {
      content.push({
        columns: [
          { width: '*', text: forma.nome, fontSize: 8 },
          { width: 60, text: formatarMoeda(forma.valor), fontSize: 8, alignment: 'right' },
        ],
        margin: [0, 0, 0, 1],
      });
    });
    content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 4, 0, 4] });

    // O que foi combinado com o cliente (digitado na hora de imprimir).
    if (dados.combinado?.trim()) {
      content.push({ text: 'COMBINADO', bold: true, fontSize: 8, margin: [0, 0, 0, 2] });
      content.push({ text: dados.combinado.trim(), fontSize: 8, margin: [0, 0, 0, 2] });
      content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 2, 0, 4] });
    }

    if (dados.acrescimo > 0.005) {
      content.push({
        columns: [
          { width: '*', text: 'ACRÉSCIMO', fontSize: 9 },
          { width: 60, text: formatarMoeda(dados.acrescimo), fontSize: 9, alignment: 'right' },
        ],
        margin: [0, 0, 0, 1],
      });
    }
    if (dados.desconto > 0.005) {
      content.push({
        columns: [
          { width: '*', text: 'DESCONTO', fontSize: 9 },
          { width: 60, text: `-${formatarMoeda(dados.desconto)}`, fontSize: 9, alignment: 'right' },
        ],
        margin: [0, 0, 0, 1],
      });
    }
    content.push({
      columns: [
        { width: '*', text: textos.total, bold: true, fontSize: 11 },
        { width: 70, text: formatarMoeda(dados.totalBaixado), bold: true, fontSize: 11, alignment: 'right' },
      ],
      margin: [0, 2, 0, 4],
    });

    // Recibo de pagamento: declaração + assinatura do parceiro que recebeu.
    if (dados.tipo === 'pagamento') {
      content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 0, 0, 4] });
      content.push({ text: declaracaoRecebimento(dados), fontSize: 8, alignment: 'justify', margin: [0, 0, 0, 22] });
      content.push({ canvas: [{ type: 'line', x1: 10, y1: 0, x2: 196, y2: 0, lineWidth: 0.6 }], margin: [0, 0, 0, 2] });
      content.push({ text: linhaAssinaturaRecebedor(dados), fontSize: 8, bold: true, alignment: 'center' });
      content.push({ text: textos.assinaturaParceiro, fontSize: 7, alignment: 'center', margin: [0, 0, 0, 6] });
    }

    content.push({ text: '-'.repeat(32), alignment: 'center', fontSize: 8, margin: [0, 0, 0, 4] });
    content.push({
      text: moment().format('DD/MM/YYYY HH:mm:ss'),
      alignment: 'center',
      fontSize: 8,
      margin: [0, 0, 0, 2],
    });
    content.push({
      text: '*** DOCUMENTO SEM VALOR FISCAL ***',
      alignment: 'center',
      bold: true,
      fontSize: 8,
      margin: [0, 2, 0, 2],
    });
    return content;
  };

  const content: Content[] = [];
  [dados, ...demais].forEach((recibo, indice) => {
    if (indice > 0) {
      content.push({ text: '- - - - - - - - corte - - - - - - - -', alignment: 'center', fontSize: 8, color: '#6b7280', margin: [0, 10, 0, 10] });
    }
    content.push(...montarConteudo(recibo));
  });

  const documentDefinition: TDocumentDefinitions = {
    pageSize: {
      width: 226.77, // 80mm
      height: 'auto' as const,
    },
    pageMargins: [10, 10, 10, 10] as Margins,
    content,
    defaultStyle: {
      font: 'Roboto',
      fontSize: 9,
    },
    compress: true,
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob: Blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
}
